import { useState, useEffect, useRef, forwardRef } from 'react';
import { cn } from '@/shared/lib/utils';
import { themed } from './themed';

function formatDisplay(paise: number): string {
  const rupees = paise / 100;
  return `₹${rupees.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatInput(paise: number): string {
  return (paise / 100).toFixed(2);
}

export type AmountDisplayProps = React.HTMLAttributes<HTMLSpanElement> & {
  value: number;
};

export const AmountDisplay = forwardRef<HTMLSpanElement, AmountDisplayProps>(
  ({ value, className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn('num', className)}
      {...props}
    >
      {formatDisplay(value)}
    </span>
  ),
);
AmountDisplay.displayName = 'Amount.Display';

export type AmountInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'max'
> & {
  value: number;
  onChange: (paise: number) => void;
  /** Optional ceiling in integer paise — typed values above this are clamped. */
  max?: number;
};

export const AmountInput = forwardRef<HTMLInputElement, AmountInputProps>(
  (
    {
      value: paise,
      onChange,
      max,
      className,
      disabled,
      placeholder = '0.00',
      onFocus: _onFocus,
      onBlur: _onBlur,
      ...props
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState(() => formatInput(paise));
    const isFocused = useRef(false);

    useEffect(() => {
      if (!isFocused.current) {
        setInputValue(formatInput(paise));
      }
    }, [paise]);

    function clampPaise(next: number): number {
      const nonNegative = Math.max(0, next);
      if (max === undefined) return nonNegative;
      return Math.min(nonNegative, max);
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const raw = e.target.value;
      if (!(/^\d*\.?\d{0,2}$/.test(raw) || raw === '')) return;

      if (raw === '') {
        setInputValue('');
        onChange(0);
        return;
      }

      const parsed = Math.round(parseFloat(raw) * 100);
      if (!Number.isFinite(parsed)) return;

      const clamped = clampPaise(parsed);
      if (clamped !== parsed) {
        setInputValue(formatInput(clamped));
        onChange(clamped);
        return;
      }

      setInputValue(raw);
      onChange(clamped);
    }

    function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
      isFocused.current = true;
      _onFocus?.(e);
    }

    function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
      isFocused.current = false;
      const clamped = clampPaise(paise);
      if (clamped !== paise) {
        onChange(clamped);
      }
      setInputValue(formatInput(clamped));
      _onBlur?.(e);
    }

    return (
      <div className={cn('relative', className)}>
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-sm font-semibold text-ink-500 select-none">
          ₹
        </span>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            themed.field.base,
            themed.field.border,
            themed.focusRing,
            'pl-8',
            disabled && 'cursor-not-allowed',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
AmountInput.displayName = 'Amount.Input';
